import { Modal } from "@9amhealth/shared";
import styled from "@emotion/styled";
import React, { CSSProperties, FC, useEffect, useRef } from "react";
import { tracker } from "src/state/state";
import { TrackingData } from "src/state/Track/TrackCubit";

const SheetContent = styled.div`
  label: SheetContent;
  padding: 2rem 1.6rem calc(var(--safe-area-inset-bottom) + 3rem);

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      width: 100%;
      max-width: 29.625rem;
      margin: 0 auto;
      padding-top: 2.5rem;
      padding-bottom: 3rem;
    }
  }
`;

const DesktopHeader = styled.div`
  display: none;

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      display: block;
      position: sticky;
      top: 0;
      z-index: 2;
      background: var(--dialog-background, #ffffff);
      border-bottom: 1px solid rgba(33, 33, 33, 0.08);
    }
  }
`;

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => unknown | Promise<unknown>;
  children?: React.ReactNode;
  trackingName?: string;
  trackingData?: TrackingData;
  style?: CSSProperties;
  overlayDragHandle?: boolean;
  className?: string;
  /** When true, the sheet becomes a centered fullscreen modal on desktop. */
  desktopFullscreen?: boolean;
  /**
   * Optional header rendered only in the desktop-fullscreen variant, above the
   * scrolling content — used for a title bar with the sheet's title and close
   * button. Hidden on mobile / when `desktopFullscreen` is false.
   */
  desktopHeader?: React.ReactNode;
}

export const BottomSheet: FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  trackingName,
  trackingData,
  style,
  overlayDragHandle,
  className,
  desktopFullscreen,
  desktopHeader
}) => {
  const wasOpen = useRef(false);
  const trackingDataRef = useRef(trackingData);
  trackingDataRef.current = trackingData;

  useEffect(() => {
    if (wasOpen.current === isOpen) {
      return;
    }
    wasOpen.current = isOpen;
    if (trackingName) {
      tracker.track(`${trackingName} ${isOpen ? "Opened" : "Closed"}`, {
        data: trackingDataRef.current ?? {}
      });
    }
  }, [isOpen, trackingName]);

  return (
    <Modal
      variant="sheet"
      isOpen={isOpen}
      onDidDismiss={() => void onClose()}
      style={style}
      overlayDragHandle={overlayDragHandle}
      className={className}
      dataAttributes={
        desktopFullscreen ? { "data-desktop-fullscreen": true } : undefined
      }
    >
      {desktopHeader && <DesktopHeader>{desktopHeader}</DesktopHeader>}
      <SheetContent data-sheet-content>{children}</SheetContent>
    </Modal>
  );
};
